import { createContext, Dispatch, useContext, useEffect, useReducer } from 'react';
import { SPOTIFY_CODE, SPOTIFY_REFRESH_TOKEN, SPOTIFY_TOKEN, SPOTIFY_TOKEN_EXPIRES_AT } from '../constants/storage';
import { addItemToStorage, getItemFromStorage, getRefreshedToken, isEmptyOrNil } from '../utils';
import SpotifyApiService from '../services/SpotifyApiService';

// Global token refresh coordination (prevents duplicate refresh attempts)
let isTokenRefreshing = false;
let refreshPromise: Promise<string> | null = null;

/**
 * Coordinated token refresh - ensures only one refresh happens at a time
 * across all contexts and components
 */
async function coordinatedTokenRefresh(): Promise<string> {
  if (refreshPromise) {
    // Another context is already refreshing, wait for it
    return refreshPromise;
  }
  
  isTokenRefreshing = true;
  
  refreshPromise = (async () => {
    try {
      const refreshToken = getItemFromStorage(SPOTIFY_REFRESH_TOKEN);
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await getRefreshedToken(refreshToken);
      const newToken = response?.data?.access_token;
      
      if (!newToken) {
        throw new Error('Failed to get new token');
      }
      
      // Update storage
      addItemToStorage(SPOTIFY_TOKEN, newToken);
      
      // Store expiry
      if (response?.data?.expires_in) {
        const expiresAt = Date.now() + response.data.expires_in * 1000;
        addItemToStorage(SPOTIFY_TOKEN_EXPIRES_AT, expiresAt.toString());
      }
      
      return newToken;
    } finally {
      isTokenRefreshing = false;
      refreshPromise = null;
    }
  })();
  
  return refreshPromise;
}

type LoginAction =
  | {
      type: 'INITIALIZE_LOGIN';
      payload: any;
    }
  | {
      type: 'SET_LOGIN';
      payload: boolean;
    }
  | {
      type: 'ADD_APP_CODE';
      payload: string;
    }
  | {
      type: 'ADD_APP_TOKEN';
      payload: {
        token: string;
        refreshToken: string;
      };
    }
  | {
      type: 'SET_USER_ID';
      payload: string;
    }
  | {
      type: 'ADD_REFRESH_TOKEN';
      payload: string;
    }
  | {
      type: 'ADD_TOKEN';
      payload: string;
    }
  | {
      type: 'SWITCH_TO_SHARED_PROFILE';
      payload: {
        token: string;
        refreshToken: string;
        activeProfileType: 'shared';
        activeProfileId: string;
        sharedProfileOwner: {
          userId: string;
          username: string;
          profileId: string;
        };
      };
    }
  | {
      type: 'SWITCH_TO_OWN_PROFILE';
      payload: {
        token: string;
        refreshToken: string;
        activeProfileType: 'own';
        activeProfileId?: string;
      };
    }
  | {
      type: 'SPOTIFY_REAUTH_REQUIRED';
      payload?: {
        profileId?: string;
        username?: string;
        spotifyID?: string | null;
      };
    }
  | {
      type: 'SPOTIFY_CONNECTED';
      payload?: {
        token?: string;
        refreshToken?: string;
        profileId?: string;
      };
    };

export type LoginContextType = {
  state: any;
  dispatch: Dispatch<LoginAction>;
};

const initialContent: any = {
  isLoggedIn: false,
  userId: '',
  username: '',
  password: '',
  email: '',
  code: '',
  token: getItemFromStorage(SPOTIFY_TOKEN) || '',
  refreshToken: getItemFromStorage(SPOTIFY_REFRESH_TOKEN) || '',
  isRememberPassword: false,
  activeProfileType: 'own',
  activeProfileId: undefined,
  sharedProfileOwner: undefined,
  spotifyNeedsReconnect: localStorage.getItem('SPOTIFY_NEEDS_RECONNECT') === 'true',
  reconnectProfile: (() => {
    try {
      return JSON.parse(localStorage.getItem('SPOTIFY_RECONNECT_PROFILE') || 'null');
    } catch {
      return null;
    }
  })(),
};

export const LoginContext = createContext<LoginContextType>({
  state: {
    ...initialContent,
  },
  dispatch: () => undefined,
});
export const useLoginContext = () => useContext(LoginContext);

const reducer = (state: any, action: any) => {
  switch (action.type) {
    case 'INITIALIZE_LOGIN':
      return {
        ...state,
        email: action.payload.email,
        password: action.payload.password,
        username: action.payload.username,
      };

    case 'SET_LOGIN':
      return {
        ...state,
        isLoggedIn: action.payload,
      };

    case 'ADD_APP_CODE':
      return {
        ...state,
        code: action.payload,
      };

    case 'ADD_APP_TOKEN':
      return {
        ...state,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
      };

    case 'SET_USER_ID':
      return {
        ...state,
        userId: action.payload,
      };

    case 'ADD_TOKEN':
      return {
        ...state,
        token: action.payload,
      };

    case 'ADD_REFRESH_TOKEN':
      return {
        ...state,
        refreshToken: action.payload,
      };

    case 'SWITCH_TO_SHARED_PROFILE':
      return {
        ...state,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        activeProfileType: action.payload.activeProfileType,
        activeProfileId: action.payload.activeProfileId,
        sharedProfileOwner: action.payload.sharedProfileOwner,
      };

    case 'SWITCH_TO_OWN_PROFILE':
      return {
        ...state,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        activeProfileType: action.payload.activeProfileType,
        activeProfileId: action.payload.activeProfileId,
        sharedProfileOwner: undefined,
        // Switching to a healthy profile must clear any other profile's reconnect UI state.
        spotifyNeedsReconnect: false,
        reconnectProfile: null,
      };

    case 'SPOTIFY_REAUTH_REQUIRED':
      return {
        ...state,
        token: '',
        refreshToken: '',
        spotifyNeedsReconnect: true,
        reconnectProfile: action.payload
          ? {
              profileId: action.payload.profileId,
              username: action.payload.username,
              spotifyID: action.payload.spotifyID,
            }
          : state.reconnectProfile,
      };

    case 'SPOTIFY_CONNECTED':
      return {
        ...state,
        token: action.payload?.token ?? state.token,
        refreshToken: action.payload?.refreshToken ?? state.refreshToken,
        spotifyNeedsReconnect: false,
        reconnectProfile: null,
        activeProfileId: action.payload?.profileId ?? state.activeProfileId,
      };

    default:
      return state;
  }
};

export const LoginContextProvider = (props: any) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialContent);

  // Same-tab reauth: localStorage alone does not re-render React
  useEffect(() => {
    const onReauth = (event: Event) => {
      const detail = (event as CustomEvent)?.detail || {};
      const identity = detail.identity || {};
      dispatch({
        type: 'SPOTIFY_REAUTH_REQUIRED',
        payload: {
          profileId: identity.profileId,
          username: identity.username,
          spotifyID: identity.spotifyID,
        },
      });
    };
    window.addEventListener('spotify:reauth-required', onReauth as EventListener);
    return () => window.removeEventListener('spotify:reauth-required', onReauth as EventListener);
  }, []);

  const token = getItemFromStorage(SPOTIFY_TOKEN);
  const refreshToken = getItemFromStorage(SPOTIFY_REFRESH_TOKEN);
  const code = getItemFromStorage(SPOTIFY_CODE);
  
  // Check if user is on a shared profile
  const spotifyUser = localStorage.getItem('spotifyUser');
  const isOnSharedProfile = spotifyUser ? JSON.parse(spotifyUser)?.isShared === true : false;

  // const [expired, setExpired] = useState(false)
  useEffect(() => {
    // UPDATED: Allow initialization when we have valid tokens, regardless of code presence
    // Code is only needed for fresh OAuth, not for existing authenticated users
    const hasToken = !isEmptyOrNil(token);
    const hasCode = !isEmptyOrNil(code);

    // Skip initialization if no token at all
    if (!hasToken) return;
    
    dispatch({
      type: 'ADD_APP_TOKEN',
      payload: {
        token: token,
        refreshToken: refreshToken,
      },
    });

    // Only set code if it exists (shared profiles may not have it)
    if (hasCode) {
      dispatch({
        type: 'ADD_APP_CODE',
        payload: code,
      });
    }

    dispatch({
      type: 'SET_LOGIN',
      payload: true,
    });

    SpotifyApiService.getInstance()
      .getCurrentUser()
      .then(resp => {
        dispatch({
          type: 'SET_USER_ID',
          payload: resp.data.id,
        });
      })
      .catch(async error => {
        console.error('❌ [LoginContext] Failed to get Spotify user:', error);
        // setExpired(true)
        // TokenManager already handled refresh attempt if token was near expiry
        // If we're here, either refresh failed or user needs to re-authenticate
        if (error.response?.data?.error?.status === 401 || error.response?.status === 400) {
          // Use coordinated refresh (prevents duplicates across contexts)
          try {
            const newToken = await coordinatedTokenRefresh();
            
            dispatch({
              type: 'ADD_TOKEN',
              payload: newToken,
            });
          } catch (err) {
            console.error('❌ Token refresh failed:', err);
          }
        }
      });
  }, [token, refreshToken, code, state.refreshToken, isOnSharedProfile]);

  return <LoginContext.Provider value={{ state, dispatch }}>{children}</LoginContext.Provider>;
};
